import React from "react";
import { MapPin, Instagram, Youtube, Star, ExternalLink, Play } from "lucide-react";
import { getTheme } from "@/lib/mediaKitThemes";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const abs = (u) => (!u ? "" : u.startsWith("http") || u.startsWith("data:") ? u : `${BACKEND}${u}`);
const PLATFORM = { instagram: "Instagram", tiktok: "TikTok", youtube: "YouTube", twitter: "X", facebook: "Facebook", linkedin: "LinkedIn" };
const SERVICE = { ugc: "UGC creation", posted: "Content posted to my audience", both: "UGC + posted content", event: "Event coverage", photography: "Photography", video: "Video production" };
function fmt(n) { n = Number(n) || 0; if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, "") + "M"; if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, "") + "K"; return String(n); }
const srcLabel = (s) => (["instagram", "connected", "verified", "tiktok"].includes(s) ? "Connected" : "Creator-provided");

function Section({ title, children, accent }) {
  return (
    <div className="mt-6">
      <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>{title}</h3>
      <div className="mt-1 h-0.5 w-8 rounded" style={{ backgroundColor: accent }} />
      <div className="mt-3">{children}</div>
    </div>
  );
}

export function MediaKitPreview({ kit, example }) {
  if (!kit) return null;
  const theme = getTheme(kit.theme);
  const accent = theme.accent;
  const socials = kit.socials || {};
  const portfolio = (kit.portfolio || []).filter((p) => p && (p.url || p.thumbnail));
  return (
    <div data-testid="media-kit-preview" className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
      {/* Header */}
      <div className="px-6 py-6 text-white" style={{ backgroundImage: `linear-gradient(135deg, ${theme.gradFrom}, ${theme.gradTo})` }}>
        <div className="flex items-center gap-4">
          {kit.profile_photo ? <img src={abs(kit.profile_photo)} alt="" className="h-16 w-16 rounded-full border-2 border-white/60 object-cover" /> : <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20 text-xl font-bold">{(kit.display_name || "?")[0]}</div>}
          <div className="min-w-0">
            <h2 className="truncate font-display text-xl font-bold">{kit.display_name}</h2>
            {kit.tagline && <p className="text-sm text-white/85">{kit.tagline}</p>}
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-white/90">
              {kit.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {kit.location}</span>}
              {(kit.niches || []).slice(0, 4).map((n) => <span key={n} className="rounded-full bg-white/15 px-2 py-0.5">{n}</span>)}
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 pb-6">
        {kit.bio && <Section title="About" accent={accent}><p className="text-sm leading-relaxed text-zinc-600">{kit.bio}</p></Section>}

        {Object.keys(socials).length > 0 && (
          <Section title="Platforms & audience" accent={accent}>
            <div className="space-y-2">
              {Object.entries(socials).map(([k, s]) => (
                <div key={k} className="flex items-center justify-between rounded-xl border border-zinc-100 bg-zinc-50 px-3 py-2">
                  <div>
                    <p className="text-sm font-semibold text-zinc-800">{PLATFORM[k] || k} {s.username && <span className="font-normal text-zinc-400">@{String(s.username).replace(/^@/, "")}</span>}</p>
                    {(s.followers || s.avg_views || s.engagement) && (
                      <p className="text-xs text-zinc-500">
                        {[s.followers && `${fmt(s.followers)} followers`, s.avg_views && `${fmt(s.avg_views)} avg views`, s.engagement && `${s.engagement}% eng.`].filter(Boolean).join(" · ")}
                        <span className="ml-1 rounded bg-zinc-200 px-1 text-[10px] text-zinc-600">{srcLabel(s.source)}{s.last_updated ? ` · ${String(s.last_updated).slice(0, 10)}` : ""}</span>
                      </p>
                    )}
                  </div>
                  {s.url && <a href={s.url} target="_blank" rel="noreferrer" style={{ color: accent }}><ExternalLink className="h-4 w-4" /></a>}
                </div>
              ))}
            </div>
          </Section>
        )}

        {(kit.manual_stats || []).length > 0 && (
          <Section title="Highlights" accent={accent}>
            <div className="grid grid-cols-2 gap-2">
              {kit.manual_stats.filter((s) => s.label || s.value).map((s, i) => (
                <div key={i} className="rounded-xl border border-zinc-100 bg-zinc-50 p-3"><p className="text-lg font-bold text-zinc-900">{s.value}</p><p className="text-xs text-zinc-500">{s.label} <span className="text-zinc-400">· creator-provided</span></p></div>
              ))}
            </div>
          </Section>
        )}

        {(kit.services || []).length > 0 && (
          <Section title="Services" accent={accent}>
            <ul className="space-y-1 text-sm text-zinc-600">
              {kit.services.map((s, i) => <li key={i} className="flex justify-between"><span>{SERVICE[s.type] || s.type}</span>{s.price_from != null && s.price_from !== "" && <span className="font-semibold text-zinc-800">from £{s.price_from}</span>}</li>)}
              {kit.gifted_ok && <li className="text-zinc-500">Open to gifted / product collaborations</li>}
            </ul>
          </Section>
        )}

        {(kit.rates_on_request || kit.rates_text) && (
          <Section title="Rates" accent={accent}><p className="text-sm text-zinc-600">{kit.rates_text && !kit.rates_on_request ? kit.rates_text : "Rates available on request."}</p></Section>
        )}

        {portfolio.length > 0 && (
          <Section title="Portfolio" accent={accent}>
            <div className="grid grid-cols-3 gap-2">
              {portfolio.map((p, i) => (
                <a key={i} href={abs(p.url)} target="_blank" rel="noreferrer" className="group relative block aspect-square overflow-hidden rounded-lg border border-zinc-100 bg-zinc-100">
                  {(p.thumbnail || p.type === "image") ? <img src={abs(p.thumbnail || p.url)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-xs text-zinc-400">Link</div>}
                  {p.type === "video" && <span className="absolute inset-0 flex items-center justify-center"><Play className="h-6 w-6 fill-white text-white drop-shadow" /></span>}
                </a>
              ))}
            </div>
          </Section>
        )}

        {(kit.collaborations || []).length > 0 && (
          <Section title="Previous collaborations" accent={accent}>
            <div className="space-y-2">
              {kit.collaborations.filter((c) => c.brand).map((c, i) => (
                <div key={i} className="flex gap-3 rounded-xl border border-zinc-100 bg-zinc-50 p-3">
                  {c.image && <img src={abs(c.image)} alt="" className="h-12 w-12 rounded object-cover" />}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-800">{c.brand} {c.date && <span className="font-normal text-zinc-400">· {c.date}</span>}</p>
                    {c.description && <p className="text-xs text-zinc-500">{c.description}</p>}
                    {c.url && <a href={c.url} target="_blank" rel="noreferrer" className="text-xs" style={{ color: accent }}>View content →</a>}
                    <p className="text-[10px] text-zinc-400">Creator-provided</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {(kit.testimonials || []).length > 0 && (
          <Section title="Testimonials" accent={accent}>
            <div className="space-y-2">
              {kit.testimonials.filter((t) => t.quote).map((t, i) => (
                <blockquote key={i} className="rounded-xl border-l-2 bg-zinc-50 px-3 py-2" style={{ borderColor: accent }}><p className="text-sm italic text-zinc-600">“{t.quote}”</p>{(t.author || t.role) && <p className="mt-1 text-xs text-zinc-400">— {[t.author, t.role].filter(Boolean).join(", ")}</p>}</blockquote>
              ))}
            </div>
          </Section>
        )}

        {kit.contact_email && <Section title="Contact" accent={accent}><a href={`mailto:${kit.contact_email}`} className="text-sm" style={{ color: accent }}>{kit.contact_email}</a></Section>}

        <div className="mt-6 flex items-center justify-center gap-1.5 border-t border-zinc-100 pt-4 text-xs text-zinc-400">
          <Star className="h-3 w-3" style={{ fill: accent, color: accent }} /> Powered by Nearfluence
        </div>
      </div>
    </div>
  );
}
